/**
 * Afronden zonder antwoord (sessie 47, deel 2).
 *
 * Niet elk bericht vraagt een antwoord: een bedankje, een bevestiging, een
 * dubbel bericht. Drie onderdelen, één gedrag, zodat de tegel en het
 * Acties-menu hetzelfde doen:
 *
 * - `AfrondKnop`: de knop met de keuze van de reden;
 * - `AfrondVoorstel`: wat Sanne of Nova voorstelt als het verhaal rond is,
 *   met één knop, plus "Toch antwoorden";
 * - `AfrondBalk`: de bevestiging met "Ongedaan maken" en de teller.
 *
 * Er gaat nooit iets naar de klant. De reden komt op de tijdlijn.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import { afrondenApi, type AfrondReden, type AfrondUitkomst } from "@/lib/werkplek-api";

/** Als de redenen (nog) niet binnen zijn: dezelfde lijst als de backend. */
export const REDENEN_TERUGVAL: AfrondReden[] = [
  { sleutel: "verhaal_rond", label: "verhaal rond" },
  { sleutel: "bedankje", label: "bedankje" },
  { sleutel: "geen_vraag", label: "geen vraag" },
  { sleutel: "spam", label: "spam of reclame" },
  { sleutel: "dubbel", label: "dubbel" },
  { sleutel: "intern_afgehandeld", label: "intern afgehandeld" },
  { sleutel: "klant_belde", label: "klant belde" },
];

function foutTekst(fout: unknown): string {
  const detail = (
    fout as { response?: { data?: { detail?: unknown } } } | undefined
  )?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  return "Afronden lukte niet. Probeer het opnieuw.";
}

function klokje(iso: string): string {
  return new Date(iso).toLocaleTimeString("nl-NL", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Amsterdam",
  });
}

/**
 * De staat van één afronding: uitvoeren, de uitkomst en ongedaan maken.
 * De uitkomst blijft staan tot er een andere zaak gekozen wordt.
 */
export function useAfronden(zaakId: string | null) {
  const queryClient = useQueryClient();
  const [uitkomst, setUitkomst] = useState<
    (AfrondUitkomst & { om: string }) | null
  >(null);
  const [nu, setNu] = useState(() => Date.now());

  useEffect(() => {
    setUitkomst(null);
  }, [zaakId]);

  const ververs = () => {
    for (const sleutel of [
      ["werkplek", "concept", zaakId],
      ["werkplek", "kaarten"],
      ["werkplek", "cijfers"],
      ["werkplek", "menutellers"],
      ["werkplek", "zaak", zaakId],
      ["werkplek", "tijdlijn", zaakId],
      // Sessie 48 (B2): "Wat opvalt" hoort de nieuwe stand te lezen; na
      // afronden stelde hij anders nog "afronden" voor.
      ["werkplek", "copiloot", "gesprek"],
    ]) {
      void queryClient.invalidateQueries({ queryKey: sleutel });
    }
  };

  const rondAf = useMutation({
    mutationFn: (reden: string) => afrondenApi.rondAf(zaakId as string, reden),
    onSuccess: (uit) => {
      setUitkomst({ ...uit, om: new Date().toISOString() });
      setNu(Date.now());
      ververs();
    },
  });
  const ongedaan = useMutation({
    mutationFn: () =>
      afrondenApi.ongedaan(zaakId as string, uitkomst?.event_id as string),
    onSuccess: () => {
      setUitkomst(null);
      ververs();
    },
  });

  const secondenOver = useMemo(() => {
    if (!uitkomst) return 0;
    return Math.max(
      0,
      Math.ceil((new Date(uitkomst.ongedaan_tot).getTime() - nu) / 1000),
    );
  }, [uitkomst, nu]);

  useEffect(() => {
    if (!uitkomst || secondenOver <= 0) return;
    const klok = window.setInterval(() => setNu(Date.now()), 1000);
    return () => window.clearInterval(klok);
  }, [uitkomst, secondenOver]);

  return { rondAf, ongedaan, uitkomst, secondenOver };
}

export type AfrondStaat = ReturnType<typeof useAfronden>;

/**
 * De keuze van de reden plus Afronden. Los te gebruiken: de tegel toont
 * hem boven zijn knop, het Acties-menu als bevestigstap.
 */
export function AfrondKeuze({
  staat,
  voorstelReden,
  onKlaar,
  plek = "boven",
}: {
  staat: AfrondStaat;
  voorstelReden?: string | null;
  onKlaar: () => void;
  /** Boven de knop (tegel) of eronder (Acties-menu). */
  plek?: "boven" | "onder";
}) {
  const [keuze, setKeuze] = useState<string>(voorstelReden ?? "verhaal_rond");
  const redenen = useQuery({
    queryKey: ["werkplek", "afronden", "redenen"],
    queryFn: afrondenApi.redenen,
    staleTime: 60 * 60_000,
  });
  const lijst = redenen.data ?? REDENEN_TERUGVAL;

  useEffect(() => {
    if (voorstelReden) setKeuze(voorstelReden);
  }, [voorstelReden]);

  return (
    <div
      className={plek === "onder" ? "wp-afrond-keuze wp-afrond-keuze-onder" : "wp-afrond-keuze"}
      role="dialog"
      aria-label="Kies een reden"
      data-testid="wp-afrond-keuze"
    >
      <p className="wp-afrond-uitleg">
        Waarom rond je af zonder antwoord? De reden komt op de tijdlijn; de
        klant krijgt niets.
      </p>
      <div role="radiogroup" aria-label="Reden">
        {lijst.map((r) => (
          <label key={r.sleutel} className="wp-afrond-reden">
            <input
              type="radio"
              name="wp-afrond-reden"
              value={r.sleutel}
              checked={keuze === r.sleutel}
              onChange={() => setKeuze(r.sleutel)}
            />
            {r.label}
            {r.sleutel === voorstelReden ? (
              <span className="wp-afrond-voorgesteld"> · voorgesteld</span>
            ) : null}
          </label>
        ))}
      </div>
      <div className="wp-afrond-knoppen">
        <button type="button" className="wp-knop" onClick={onKlaar}>
          Annuleren
        </button>
        <button
          type="button"
          className="wp-knop wp-knop-primair"
          data-testid="wp-afrond-bevestig"
          disabled={staat.rondAf.isPending}
          onClick={() => staat.rondAf.mutate(keuze, { onSuccess: onKlaar })}
        >
          Afronden
        </button>
      </div>
      {staat.rondAf.isError ? (
        <p className="wp-tegel-fout">{foutTekst(staat.rondAf.error)}</p>
      ) : null}
    </div>
  );
}

/** De knop met de keuze van de reden. */
export function AfrondKnop({
  staat,
  voorstelReden,
  uit,
  compact = false,
}: {
  staat: AfrondStaat;
  voorstelReden?: string | null;
  /** Waarom de knop niet kan; dan staat hij uit met die tekst erbij. */
  uit?: string | null;
  compact?: boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    <span className="wp-afrond" data-testid="wp-afrond">
      <button
        type="button"
        className={compact ? "wp-knop wp-tegel-knop-klein" : "wp-knop"}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        disabled={Boolean(uit) || staat.rondAf.isPending}
        title={uit ?? "Zet de zaak op afgerond; er gaat niets naar de klant."}
        data-testid="wp-afrond-knop"
      >
        Afronden zonder antwoord
      </button>
      {uit ? <span className="wp-afrond-uit">{uit}</span> : null}
      {open ? (
        <AfrondKeuze
          staat={staat}
          voorstelReden={voorstelReden}
          onKlaar={() => setOpen(false)}
        />
      ) : null}
    </span>
  );
}

/** Wat Sanne of Nova voorstelt als het verhaal rond is. */
export function AfrondVoorstel({
  staat,
  reden,
  label,
  door,
  onTochAntwoorden,
  tochBezig,
}: {
  staat: AfrondStaat;
  reden: string;
  label: string;
  door: string;
  onTochAntwoorden: () => void;
  tochBezig: boolean;
}) {
  return (
    <div className="wp-afrond-voorstel" data-testid="wp-afrond-voorstel" role="status">
      <p>
        <strong>{door} stelt voor: afronden zonder antwoord, {label}.</strong>{" "}
        Het laatste bericht van de klant bevat geen vraag en geen nieuw
        probleem. We hoeven niet het laatste woord te hebben.
      </p>
      <div className="wp-afrond-knoppen">
        <button
          type="button"
          className="wp-knop wp-knop-primair"
          data-testid="wp-afrond-voorstel-knop"
          disabled={staat.rondAf.isPending}
          onClick={() => staat.rondAf.mutate(reden)}
        >
          Afronden zonder antwoord: {label}
        </button>
        <button
          type="button"
          className="wp-knop"
          data-testid="wp-afrond-toch"
          disabled={tochBezig}
          onClick={onTochAntwoorden}
        >
          Toch antwoorden
        </button>
      </div>
      {staat.rondAf.isError ? (
        <p className="wp-tegel-fout">{foutTekst(staat.rondAf.error)}</p>
      ) : null}
    </div>
  );
}

/** De bevestiging, met Ongedaan maken zolang het venster loopt. */
export function AfrondBalk({ staat }: { staat: AfrondStaat }) {
  const { uitkomst, secondenOver, ongedaan } = staat;
  if (!uitkomst) return null;
  return (
    <div className="wp-verstuurd wp-afrond-balk" role="status" aria-live="polite" data-testid="wp-afrond-balk">
      <span>
        <strong>{uitkomst.waarom}</strong> om {klokje(uitkomst.om)}
        {" · "}geen antwoord verstuurd
        {uitkomst.mails_gesloten > 0
          ? ` · ${uitkomst.mails_gesloten === 1 ? "1 mail" : `${uitkomst.mails_gesloten} mails`} gesloten`
          : ""}
      </span>
      {secondenOver > 0 ? (
        <button
          type="button"
          className="wp-knop wp-tegel-knop-klein"
          style={{ marginLeft: "auto" }}
          data-testid="wp-afrond-ongedaan"
          onClick={() => ongedaan.mutate()}
          disabled={ongedaan.isPending || !uitkomst.event_id}
        >
          Ongedaan maken <span className="wp-verstuurd-tel">{secondenOver}</span>
        </button>
      ) : (
        <span style={{ marginLeft: "auto", color: "var(--wp-tekst2)" }}>
          Afgerond. Schrijft de klant weer, dan gaat de zaak vanzelf open.
        </span>
      )}
      {ongedaan.isError ? (
        <p className="wp-tegel-fout" style={{ flexBasis: "100%" }}>
          {foutTekst(ongedaan.error)}
        </p>
      ) : null}
    </div>
  );
}
